import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Smartphone, Save, Plus, Loader2, Apple, Tablet } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

interface AppSystemEntry {
  id: string;
  app_name: string | null;
  platform: string | null;
  last_version: string;
  updated_at: string;
}

interface EditState {
  [id: string]: string;
}

const platformIcon = (platform: string | null) => {
  if (platform === "ios") return <Apple className="h-4 w-4" />;
  if (platform === "android") return <Tablet className="h-4 w-4" />;
  return <Smartphone className="h-4 w-4" />;
};

const platformLabel = (platform: string | null) => {
  if (platform === "ios") return "iOS";
  if (platform === "android") return "Android";
  return platform || "N/A";
};

export default function SuperAdminAppVersions() {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [editState, setEditState] = useState<EditState>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({ app_name: "", platform: "ios", last_version: "" });
  const { t } = useTranslation(["superadmin2", "common"]);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["app-system"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_system")
        .select("*")
        .order("app_name")
        .order("platform");
      if (error) throw error;
      return data as AppSystemEntry[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, version }: { id: string; version: string }) => {
      const { error } = await supabase
        .from("app_system")
        .update({ last_version: version, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-system"] });
      toast({ title: t("appVersions.versionUpdatedTitle"), description: t("appVersions.versionUpdatedDesc") });
    },
    onError: (err: Error) => {
      toast({ title: t("appVersions.errorTitle"), description: err.message, variant: "destructive" });
    },
  });

  const insertMutation = useMutation({
    mutationFn: async (entry: { app_name: string; platform: string; last_version: string }) => {
      const { error } = await supabase.from("app_system").insert(entry);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-system"] });
      setShowAddForm(false);
      setNewEntry({ app_name: "", platform: "ios", last_version: "" });
      toast({ title: t("appVersions.entryAddedTitle"), description: t("appVersions.entryAddedDesc") });
    },
    onError: (err: Error) => {
      toast({ title: t("appVersions.errorTitle"), description: err.message, variant: "destructive" });
    },
  });

  // Group by app_name
  const grouped = entries.reduce<Record<string, AppSystemEntry[]>>((acc, e) => {
    const key = e.app_name || t("appVersions.unnamed");
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  const handleSave = (id: string) => {
    const version = editState[id];
    if (!version) return;
    // La saisie n'est effacée qu'une fois l'enregistrement confirmé :
    // en cas d'échec elle était perdue et le champ revenait en silence à
    // l'ancienne version.
    updateMutation.mutate(
      { id, version },
      {
        onSuccess: () =>
          setEditState((s) => {
            const copy = { ...s };
            delete copy[id];
            return copy;
          }),
      }
    );
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("appVersions.title")}</h1>
            <p className="text-muted-foreground text-sm">{t("appVersions.subtitle")}</p>
          </div>
          <Button onClick={() => setShowAddForm(!showAddForm)} size={isMobile ? "sm" : "default"}>
            <Plus className="h-4 w-4 mr-1" />
            {t("appVersions.add")}
          </Button>
        </div>

        {showAddForm && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("appVersions.newEntry")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div className="space-y-1">
                  <Label>{t("appVersions.appName")}</Label>
                  <Input
                    value={newEntry.app_name}
                    onChange={(e) => setNewEntry((s) => ({ ...s, app_name: e.target.value }))}
                    placeholder={t("appVersions.appNamePlaceholder")}
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t("appVersions.platform")}</Label>
                  <Select value={newEntry.platform} onValueChange={(v) => setNewEntry((s) => ({ ...s, platform: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ios">iOS</SelectItem>
                      <SelectItem value="android">Android</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{t("appVersions.minVersion")}</Label>
                  <Input
                    value={newEntry.last_version}
                    onChange={(e) => setNewEntry((s) => ({ ...s, last_version: e.target.value }))}
                    placeholder={t("appVersions.minVersionPlaceholder")}
                  />
                </div>
                <Button
                  onClick={() => insertMutation.mutate(newEntry)}
                  disabled={!newEntry.app_name || !newEntry.last_version || insertMutation.isPending}
                >
                  {insertMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("appVersions.create")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : Object.keys(grouped).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {t("appVersions.noAppsRegistered")}
            </CardContent>
          </Card>
        ) : (
          Object.entries(grouped).map(([appName, items]) => (
            <Card key={appName}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Smartphone className="h-5 w-5 text-primary" />
                  {appName}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {items.map((item) => {
                  const edited = editState[item.id];
                  const currentVersion = edited ?? item.last_version;
                  const isDirty = edited !== undefined && edited !== item.last_version;

                  return (
                    <div key={item.id} className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-[100px]">
                        {platformIcon(item.platform)}
                        <span className="font-medium text-sm">{platformLabel(item.platform)}</span>
                      </div>
                      <Input
                        className="max-w-[160px]"
                        value={currentVersion}
                        onChange={(e) =>
                          setEditState((s) => ({ ...s, [item.id]: e.target.value }))
                        }
                      />
                      <Button
                        size="sm"
                        variant={isDirty ? "default" : "outline"}
                        disabled={!isDirty || updateMutation.isPending}
                        onClick={() => handleSave(item.id)}
                      >
                        {updateMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-1" />
                            {t("appVersions.save")}
                          </>
                        )}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {t("appVersions.updatedAt", { date: new Date(item.updated_at).toLocaleDateString("fr-FR") })}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </SuperAdminLayout>
  );
}
